/**
 * ArticleContent - Renders BlockNote JSON content as static HTML
 * This component takes the body_json from an article and renders it
 * without the editor functionality (read-only display).
 */

interface BlockContent {
  type: string
  text?: string
  styles?: Record<string, boolean>
}

interface Block {
  id: string
  type: string
  props?: Record<string, unknown>
  content?: BlockContent[]
  children?: Block[]
}

interface ArticleContentProps {
  bodyJson?: string | null
  bodyMarkdown?: string // Fallback for old articles
}

// Helper to extract video embed URL
function extractVideoEmbedUrl(url: string): string | null {
  // YouTube patterns
  const youtubePatterns = [
    /(?:youtube\.com\/watch\?v=|youtu\.be\/|youtube\.com\/embed\/)([a-zA-Z0-9_-]{11})/,
    /youtube\.com\/shorts\/([a-zA-Z0-9_-]{11})/,
  ]

  for (const pattern of youtubePatterns) {
    const match = url.match(pattern)
    if (match) {
      return `https://www.youtube.com/embed/${match[1]}`
    }
  }

  // Vimeo patterns
  const vimeoPattern = /(?:vimeo\.com\/)(\d+)/
  const vimeoMatch = url.match(vimeoPattern)
  if (vimeoMatch) {
    return `https://player.vimeo.com/video/${vimeoMatch[1]}`
  }

  return null
}

// Helper to render blocks with proper list grouping
function renderBlocksWithListGrouping(blocks: Block[]): React.ReactNode[] {
  const renderedBlocks: React.ReactNode[] = []
  let currentListItems: Block[] = []
  let currentListType: 'bullet' | 'numbered' | 'check' | null = null

  const flushList = () => {
    if (currentListItems.length > 0 && currentListType) {
      const ListTag = currentListType === 'numbered' ? 'ol' : 'ul'
      renderedBlocks.push(
        <ListTag key={`list-${renderedBlocks.length}`} className="mb-4">
          {currentListItems.map((item) => renderBlock(item))}
        </ListTag>
      )
      currentListItems = []
      currentListType = null
    }
  }

  blocks.forEach((block, index) => {
    const isBullet = block.type === 'bulletListItem'
    const isNumbered = block.type === 'numberedListItem'
    const isCheck = block.type === 'checkListItem'
    const isListItem = isBullet || isNumbered || isCheck

    if (isListItem) {
      const listType = isBullet ? 'bullet' : isNumbered ? 'numbered' : 'check'
      if (currentListType && currentListType !== listType) {
        flushList()
      }
      currentListType = listType
      currentListItems.push(block)
    } else {
      flushList()
      const rendered = renderBlock(block)
      if (rendered) {
        renderedBlocks.push(<span key={`block-${index}`}>{rendered}</span>)
      }
    }
  })

  flushList()
  return renderedBlocks
}

// Render inline content with styles
function renderInlineContent(content: BlockContent[]): React.ReactNode {
  return content.map((item, index) => {
    if (item.type === 'text' && item.text) {
      let element: React.ReactNode = item.text

      if (item.styles?.bold) {
        element = <strong key={`bold-${index}`}>{element}</strong>
      }
      if (item.styles?.italic) {
        element = <em key={`italic-${index}`}>{element}</em>
      }
      if (item.styles?.underline) {
        element = <u key={`underline-${index}`}>{element}</u>
      }
      if (item.styles?.strike) {
        element = <s key={`strike-${index}`}>{element}</s>
      }
      if (item.styles?.code) {
        element = <code key={`code-${index}`} className="bg-gray-100 px-1 rounded text-sm">{element}</code>
      }

      return <span key={index}>{element}</span>
    }

    if (item.type === 'link') {
      return (
        <a
          key={index}
          href={(item as unknown as { href: string }).href}
          className="text-tessan-primary hover:underline"
          target="_blank"
          rel="noopener noreferrer"
        >
          {(item as unknown as { content: BlockContent[] }).content?.map((c, i) => (
            <span key={i}>{c.text}</span>
          ))}
        </a>
      )
    }

    return null
  })
}

// Render a single block
function renderBlock(block: Block, key?: string | number): React.ReactNode {
  const content = block.content || []
  const props = block.props || {}
  const elementKey = key ?? block.id

  switch (block.type) {
    case 'paragraph':
      return (
        <p className="mb-4 text-gray-700 leading-relaxed">
          {renderInlineContent(content)}
        </p>
      )

    case 'heading': {
      const level = (props.level as number) || 1
      const headingClasses: Record<number, string> = {
        1: 'text-3xl font-heading font-bold text-gray-900 mt-8 mb-4',
        2: 'text-2xl font-heading font-semibold text-gray-900 mt-6 mb-3',
        3: 'text-xl font-heading font-semibold text-gray-900 mt-4 mb-2',
      }
      const className = headingClasses[level] || headingClasses[1]
      const inlineContent = renderInlineContent(content)

      if (level === 1) return <h1 className={className}>{inlineContent}</h1>
      if (level === 2) return <h2 className={className}>{inlineContent}</h2>
      return <h3 className={className}>{inlineContent}</h3>
    }

    case 'bulletListItem':
      return (
        <li key={elementKey} className="ml-6 mb-2 text-gray-700 list-disc">
          {renderInlineContent(content)}
        </li>
      )

    case 'numberedListItem':
      return (
        <li key={elementKey} className="ml-6 mb-2 text-gray-700 list-decimal">
          {renderInlineContent(content)}
        </li>
      )

    case 'checkListItem': {
      const checked = props.checked as boolean
      return (
        <li key={elementKey} className="ml-6 mb-2 text-gray-700 flex items-start gap-2">
          <input type="checkbox" checked={checked} readOnly className="mt-1" />
          <span className={checked ? 'line-through text-gray-500' : ''}>
            {renderInlineContent(content)}
          </span>
        </li>
      )
    }

    case 'codeBlock':
      return (
        <pre className="bg-gray-900 text-gray-100 p-4 rounded-lg overflow-x-auto mb-4">
          <code>{content.map(c => c.text).join('')}</code>
        </pre>
      )

    case 'table':
      const tableContent = props.tableContent as { rows: Array<{ cells: Array<Array<{ text: string }>> }> }
      if (!tableContent?.rows) return null
      return (
        <div className="overflow-x-auto mb-4">
          <table className="min-w-full border border-gray-200 rounded-lg">
            <tbody>
              {tableContent.rows.map((row, rowIndex) => (
                <tr key={rowIndex} className={rowIndex === 0 ? 'bg-gray-50' : ''}>
                  {row.cells.map((cell, cellIndex) => {
                    const CellTag = rowIndex === 0 ? 'th' : 'td'
                    return (
                      <CellTag
                        key={cellIndex}
                        className="border border-gray-200 px-4 py-2 text-left"
                      >
                        {cell.map(c => c.text).join('')}
                      </CellTag>
                    )
                  })}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )

    // Custom blocks - handle both 'video' and legacy 'videoEmbed'
    case 'video':
    case 'videoEmbed': {
      const videoUrl = props.url as string
      if (!videoUrl) return null
      const embedUrl = extractVideoEmbedUrl(videoUrl)
      if (!embedUrl) {
        return (
          <div className="p-4 border border-gray-200 rounded-lg bg-gray-50 mb-4">
            <p className="text-sm text-gray-500">Vidéo non disponible</p>
          </div>
        )
      }
      return (
        <div className="aspect-video w-full rounded-lg overflow-hidden bg-black mb-4">
          <iframe
            src={embedUrl}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            title="Video embed"
          />
        </div>
      )
    }

    case 'twoColumns': {
      const leftContentRaw = props.leftContent as string || '[]'
      const rightContentRaw = props.rightContent as string || '[]'

      // Parse nested column content (can be JSON blocks or legacy plain text)
      const parseColumnContent = (content: string): Block[] => {
        try {
          const parsed = JSON.parse(content)
          return Array.isArray(parsed) ? parsed : []
        } catch {
          // Legacy plain text - convert to paragraph blocks
          if (content && content !== '[]') {
            return content.split('\n').filter(Boolean).map((line, i) => ({
              id: `legacy-${i}`,
              type: 'paragraph',
              content: [{ type: 'text', text: line }],
            }))
          }
          return []
        }
      }

      const leftBlocks = parseColumnContent(leftContentRaw)
      const rightBlocks = parseColumnContent(rightContentRaw)

      return (
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-4">
          <div className="prose prose-sm max-w-none">
            {renderBlocksWithListGrouping(leftBlocks)}
          </div>
          <div className="prose prose-sm max-w-none">
            {renderBlocksWithListGrouping(rightBlocks)}
          </div>
        </div>
      )
    }

    // Handle both 'image' and legacy 'imageUrl'
    case 'image':
    case 'imageUrl': {
      const imageUrlProp = props.url as string
      const alt = props.alt as string || 'Image'
      const caption = props.caption as string
      if (!imageUrlProp) return null
      return (
        <figure className="mb-4">
          <img
            src={imageUrlProp}
            alt={alt}
            className="w-full rounded-lg"
          />
          {caption && (
            <figcaption className="mt-2 text-sm text-gray-500 text-center italic">
              {caption}
            </figcaption>
          )}
        </figure>
      )
    }

    default:
      // For unknown block types, try to render content if available
      if (content.length > 0) {
        return (
          <div className="mb-4 text-gray-700">
            {renderInlineContent(content)}
          </div>
        )
      }
      return null
  }
}

// Fallback: render old markdown content
function formatMarkdown(body: string): string {
  let html = body
    .replace(/^## (.+)$/gm, '<h2 class="text-2xl font-heading font-semibold text-gray-900 mt-6 mb-3">$1</h2>')
    .replace(/^### (.+)$/gm, '<h3 class="text-xl font-heading font-semibold text-gray-900 mt-4 mb-2">$1</h3>')
    .replace(/\*\*(.+?)\*\*/g, '<strong>$1</strong>')
    .replace(/\*(.+?)\*/g, '<em>$1</em>')
    .replace(/^- (.+)$/gm, '<li class="ml-6 mb-2 text-gray-700 list-disc">$1</li>')
    .replace(/(<li.*<\/li>\n?)+/g, '<ul class="mb-4">$&</ul>')

  // Paragraphs
  html = html.split('\n\n').map(block => {
    if (block.startsWith('<') || block.trim() === '') {
      return block
    }
    return `<p class="mb-4 text-gray-700 leading-relaxed">${block}</p>`
  }).join('\n')

  return html
}

export function ArticleContent({ bodyJson, bodyMarkdown }: ArticleContentProps) {
  // Try to parse body_json first
  if (bodyJson) {
    try {
      const blocks: Block[] = JSON.parse(bodyJson)

      // Group list items together
      const renderedBlocks: React.ReactNode[] = []
      let currentListItems: Block[] = []
      let currentListType: 'bullet' | 'numbered' | 'check' | null = null

      const flushList = () => {
        if (currentListItems.length > 0 && currentListType) {
          const ListTag = currentListType === 'numbered' ? 'ol' : 'ul'
          renderedBlocks.push(
            <ListTag key={`list-${renderedBlocks.length}`} className="mb-4">
              {currentListItems.map((item) => renderBlock(item))}
            </ListTag>
          )
          currentListItems = []
          currentListType = null
        }
      }

      blocks.forEach((block, index) => {
        const isBullet = block.type === 'bulletListItem'
        const isNumbered = block.type === 'numberedListItem'
        const isCheck = block.type === 'checkListItem'
        const isListItem = isBullet || isNumbered || isCheck

        if (isListItem) {
          const listType = isBullet ? 'bullet' : isNumbered ? 'numbered' : 'check'
          if (currentListType && currentListType !== listType) {
            flushList()
          }
          currentListType = listType
          currentListItems.push(block)
        } else {
          flushList()
          const rendered = renderBlock(block)
          if (rendered) {
            renderedBlocks.push(<span key={`block-${index}`}>{rendered}</span>)
          }
        }
      })

      flushList()

      return (
        <>
          <style>{`
            .article-content {
              counter-reset: article-list-counter;
            }
            .article-content ol {
              list-style: none;
              padding-left: 0;
            }
            .article-content ol > li {
              counter-increment: article-list-counter;
              list-style: none;
            }
            .article-content ol > li::before {
              content: counter(article-list-counter) ". ";
              font-weight: 500;
              margin-right: 0.25rem;
            }
          `}</style>
          <div className="article-content">{renderedBlocks}</div>
        </>
      )
    } catch (e) {
      console.warn('Failed to parse body_json, falling back to markdown', e)
    }
  }

  // Fallback to markdown rendering
  if (bodyMarkdown) {
    return (
      <div
        className="article-content"
        dangerouslySetInnerHTML={{ __html: formatMarkdown(bodyMarkdown) }}
      />
    )
  }

  return null
}

export default ArticleContent
